/**
 * Legende for innsikt-overlays: modus-navn, felles fargeskala (blå→gul→rød)
 * med min/maks-etiketter per modus, og «Ukjent»-markør for lokasjoner uten data.
 * Plasseres nederst til høyre i både 2D-canvaset og 3D-scenen.
 */

import React from 'react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { InsightMode } from '../hooks/useLayoutReducer';
import { insightColor, INSIGHT_UNKNOWN_COLOR } from '../utils/insightUtils';

/** Horisontal gradient over hele skalaen (0 → 1) — samme farger som rendrerne bruker */
const GRADIENT = `linear-gradient(to right, ${[0, 0.25, 0.5, 0.75, 1]
  .map(v => insightColor(v))
  .join(', ')})`;

const InsightLegend: React.FC = () => {
  const { state } = useLayoutEditor();
  const { t } = useTranslation();

  if (state.insightMode === 'none') return null;

  const mode: Exclude<InsightMode, 'none'> = state.insightMode;
  const maxRaw = state.insights ? Math.round(state.insights.maxRaw) : 0;
  const days = state.insights?.days ?? state.insightDays;

  // Modus-navn — samme etiketter som i header-velgeren
  const modeLabels: Record<Exclude<InsightMode, 'none'>, string> = {
    heatmap: t('manager.modules.warehouseLayout.insights.modeHeatmap', {}, 'Aktivitet'),
    fill: t('manager.modules.warehouseLayout.insights.modeFill', {}, 'Fyllingsgrad'),
    empty: t('manager.modules.warehouseLayout.insights.modeEmpty', {}, 'Tomme'),
    dead: t('manager.modules.warehouseLayout.insights.modeDead', {}, 'Dødlager'),
    countage: t('manager.modules.warehouseLayout.insights.modeCountage', {}, 'Telle-alder'),
  };

  // Min/maks-etiketter med enhet per modus
  let minLabel: string;
  let maxLabel: string;
  switch (mode) {
    case 'heatmap':
      minLabel = '0';
      maxLabel = t(
        'manager.modules.warehouseLayout.insights.legendHeatmapMax',
        { '0': maxRaw, '1': days },
        `${maxRaw} bevegelser/${days}d`
      );
      break;
    case 'fill':
      minLabel = '0 %';
      maxLabel = '100 %';
      break;
    case 'empty':
      minLabel = t('manager.modules.warehouseLayout.insights.legendInUse', {}, 'I bruk');
      maxLabel = t('manager.modules.warehouseLayout.insights.legendEmpty', {}, 'Tom');
      break;
    default: // 'dead' og 'countage'
      minLabel = '0';
      maxLabel = t(
        'manager.modules.warehouseLayout.insights.legendDaysMax',
        { '0': maxRaw },
        `${maxRaw} dager`
      );
      break;
  }

  return (
    <div className="absolute bottom-3 right-3 z-10 bg-gray-900/85 border border-gray-700 rounded-lg px-3 py-2 shadow-lg pointer-events-none select-none">
      <div className="text-xs font-semibold text-gray-200 mb-1.5">{modeLabels[mode]}</div>
      <div className="w-44 h-2.5 rounded" style={{ background: GRADIENT }} />
      <div className="flex justify-between gap-3 mt-1 text-[10px] text-gray-400">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-gray-400">
        <span
          className="inline-block w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: INSIGHT_UNKNOWN_COLOR }}
        />
        {t('manager.modules.warehouseLayout.insights.legendUnknown', {}, 'Ukjent')}
      </div>
    </div>
  );
};

export default InsightLegend;
